// Centralized API service for all HTTP requests

const BASE_URL = 'https://www.traffixcloud.com/upload';

/**
 * Generic fetch wrapper with error handling
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @returns {Promise} - Promise resolving to JSON data
 */
const fetchJSON = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching ${url}:`, error);
    throw error;
  }
};

/**
 * Fetch text data (for CSV files)
 * @param {string} url - The URL to fetch
 * @returns {Promise<string>} - Promise resolving to text data
 */
const fetchText = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.text();
  } catch (error) {
    console.error(`Error fetching ${url}:`, error);
    throw error;
  }
};

// ==================== Traffic Data APIs ====================

/**
 * Parse CSV text to JSON array
 * @param {string} csvText - CSV text data
 * @returns {Array} - Array of objects
 */
const parseCSV = (csvText) => {
  const lines = csvText.trim().split('\n');
  if (lines.length === 0) return [];

  const headers = lines[0].split(',');
  const result = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',');
    const obj = {};
    headers.forEach((header, index) => {
      const value = values[index];
      const headerName = header.trim();

      // Keep certain fields as strings (dates, IDs, times)
      if (headerName === 'day_group_label' || headerName === 'UnifiedID' ||
          headerName === 't_start' || headerName === 't_end') {
        obj[headerName] = value;
      } else {
        // Try to parse numbers for other fields
        const numValue = parseFloat(value);
        obj[headerName] = isNaN(numValue) ? value : numValue;
      }
    });
    result.push(obj);
  }

  return result;
};

/**
 * Fetch traffic data from lavagna.csv (new data with 2024-08-01 to 2024-08-09 dates)
 * @returns {Promise<Array>} - Metrics data
 */
export const fetchMetricsData = async () => {
  try {
    const csvText = await fetchText('/new_data/lavagna.csv');
    return parseCSV(csvText);
  } catch (error) {
    console.error('Error loading lavagna.csv, falling back to metrics.json:', error);
    return fetchJSON('/metrics.json');
  }
};

/**
 * DEPRECATED: Fetch traffic data (5-minute intervals) in CSV format
 * Now using fetchMetricsData() instead
 * @returns {Promise<string>} - CSV text data
 */
export const fetchTrafficCSV = () => {
  // Redirect to metrics.json
  return fetchMetricsData();
};

/**
 * DEPRECATED: Fetch legacy traffic zone data (JSON fallback)
 * Now using fetchMetricsData() instead
 * @returns {Promise<object>} - Traffic zone data
 */
export const fetchTrafficZoneData = () => {
  // Redirect to metrics.json
  return fetchMetricsData();
};

/**
 * Fetch network offset data (converted from GPKG to GeoJSON)
 * @returns {Promise<object>} - Network offset data as GeoJSON
 */
export const fetchNetworkOffsetData = async () => {
  try {
    console.log('Loading network_offset.geojson...');

    // Fetch the pre-converted GeoJSON file
    const geojson = await fetchJSON('/network_offset.geojson');

    console.log('Successfully loaded network_offset.geojson:', {
      features: geojson.features?.length || 0,
      type: geojson.type
    });

    // Map the GPKG attributes to match expected format
    if (geojson.features) {
      geojson.features = geojson.features.map(feature => ({
        ...feature,
        properties: {
          ...feature.properties,
          // Map UnifiedID to hash for compatibility
          hash: feature.properties.UnifiedID,
          // Keep other important properties
          StreetName: feature.properties.StreetName,
          SpeedLimit: feature.properties.SpeedLimit,
          Length: feature.properties.Length_sum,
          FRC: feature.properties.FRC,
          'Segment Id': feature.properties.UnifiedID
        }
      }));
    }

    return geojson;

  } catch (error) {
    console.error('Error loading network_offset.geojson:', error);
    console.warn('Falling back to archi_zona.geojson...');

    // Fallback to existing GeoJSON data
    return fetchJSON('/archi_zona.geojson')
      .catch(fallbackError => {
        console.error('Error loading fallback GeoJSON:', fallbackError);
        throw fallbackError;
      });
  }
};

/**
 * DEPRECATED: Fetch map GeoJSON data
 * Now using fetchNetworkOffsetData() instead
 * @returns {Promise<object>} - GeoJSON map data
 */
export const fetchMapGeoJSON = () => {
  return fetchNetworkOffsetData();
};

/**
 * DEPRECATED: Fetch legacy archi zona GeoJSON (fallback)
 * Now using fetchNetworkOffsetData() instead
 * @returns {Promise<object>} - GeoJSON data
 */
export const fetchArchiZonaGeoJSON = () => {
  return fetchNetworkOffsetData();
};

// ==================== Environmental Data APIs ====================

/**
 * Fetch environmental data (1-hour intervals)
 * @returns {Promise<Array>} - Array of environmental data points
 */
export const fetchEnvironmentData = () => {
  return fetchJSON(`${BASE_URL}/new_data/Environment_1h.json`);
};

/**
 * Fetch legacy environmental daily data
 * @param {string} date - Date in YYYYMMDD format
 * @returns {Promise<object>} - Daily environmental data
 */
export const fetchEnvironmentDailyData = (date) => {
  return fetchJSON(`/ambientali_finale_tutto/${date}.json`);
};

/**
 * Fetch environmental data index (list of available dates)
 * @returns {Promise<Array>} - Array of available dates
 */
export const fetchEnvironmentIndex = () => {
  return fetchJSON('/ambientali_finale_tutto/media_giornaliera.json');
};

/**
 * Fetch legacy environmental index
 * @returns {Promise<Array>} - Array of available files
 */
export const fetchEnvironmentFileIndex = () => {
  return fetchJSON('/ambientali_finale_tutto/index.json');
};

// ==================== Noise/Audio Data APIs ====================

/**
 * Fetch noise data (1-minute intervals)
 * @returns {Promise<Array>} - Array of noise data points
 */
export const fetchNoiseData = () => {
  return fetchJSON(`${BASE_URL}/new_data/Noise_1min.json`);
};

/**
 * Fetch detected sounds classification data
 * @returns {Promise<object>} - Object with sound classifications
 */
export const fetchDetectedSounds = () => {
  return fetchJSON('/new_data/detected_sounds.json');
};

/**
 * Fetch legacy noise data for a specific date
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<object>} - Daily noise data
 */
export const fetchNoiseDailyData = (date) => {
  return fetchJSON(`/json_giorni/${date}.json`);
};

/**
 * Fetch legacy noise data index
 * @returns {Promise<Array>} - Array of available dates
 */
export const fetchNoiseIndex = () => {
  return fetchJSON('/json_giorni/json_index.json');
};

// ==================== Vibration Data APIs ====================

/**
 * Fetch vibration data (1-minute intervals)
 * @returns {Promise<Array>} - Array of vibration data points
 */
export const fetchVibrationData = () => {
  return fetchJSON(`${BASE_URL}/new_data/Vibration_1min.json`);
};

/**
 * Fetch legacy IMU (vibration) data for a specific file
 * @param {string} filename - Filename to fetch
 * @returns {Promise<object>} - IMU data
 */
export const fetchIMUData = (filename) => {
  return fetchJSON(`/imu_json/${filename}`);
};

/**
 * Fetch IMU data index
 * @returns {Promise<Array>} - Array of available IMU files
 */
export const fetchIMUIndex = () => {
  return fetchJSON('/imu_json/index.json');
};

// ==================== Vehicle/Trip Data APIs ====================

/**
 * Fetch animated vehicles GeoJSON data
 * @returns {Promise<object>} - GeoJSON with vehicle trajectories
 */
export const fetchAnimatedVehicles = () => {
  return fetchJSON('/animated_vehicles.geojson');
};

/**
 * Fetch trips data (legacy)
 * @param {string} url - URL to trips data
 * @returns {Promise<object>} - Trips data
 */
export const fetchTripsData = (url) => {
  return fetchJSON(url);
};

// ==================== Point Cloud Data APIs ====================

/**
 * Fetch 3D point cloud data
 * @returns {Promise<object>} - Point cloud data
 */
export const fetchPointCloud3D = () => {
  return fetchJSON('/NuvolaPunti3d.json');
};

// ==================== Building Data APIs ====================

/**
 * Fetch building data for Genova
 * @returns {Promise<object>} - Building polygon data
 */
export const fetchBuildingsData = () => {
  return fetchJSON('/buildings_genova.json');
};

// ==================== Helper Functions ====================

/**
 * Fetch data with automatic fallback
 * @param {function} primaryFetch - Primary fetch function
 * @param {function} fallbackFetch - Fallback fetch function
 * @returns {Promise} - Promise resolving to data from primary or fallback
 */
export const fetchWithFallback = async (primaryFetch, fallbackFetch) => {
  try {
    return await primaryFetch();
  } catch (error) {
    console.warn('Primary fetch failed, trying fallback...', error);
    return await fallbackFetch();
  }
};

/**
 * Batch fetch multiple resources in parallel
 * @param {Array<function>} fetchFunctions - Array of fetch functions
 * @returns {Promise<Array>} - Array of results from all fetches
 */
export const batchFetch = async (fetchFunctions) => {
  return Promise.all(fetchFunctions.map(fn => fn().catch(err => {
    console.error('Batch fetch error:', err);
    return null;
  })));
};
